import React, { useCallback, useContext, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import arrayMove from 'array-move';
import { FormContext } from '../../hooks/useForm';
import Column from '../../helpers/columns';
import { getStations } from '../../services/taxonomies/stations';
import useDictionaries from '../../hooks/useDictionaries';
import DragSortingTable from '../DragSortingTable';

const dictionaries = {
  stations: getStations,
};

const StationsSelection = ({ canEdit = true }) => {
  const form = useContext(FormContext);
  const { t } = useTranslation();

  const { value, setValue } = form;

  const [{ stations }] = useDictionaries(dictionaries);

  const stationsMeta = useMemo(
    () => value.stationsMeta || [],
    [value.stationsMeta],
  );

  // Keep 'stations' in sync with unique stationIds from stationsMeta
  useEffect(() => {
    const uniqueStationIds = Array.from(
      new Set(stationsMeta.map((s) => s.stationId)),
    );
    setValue('stations', uniqueStationIds);
  }, [stationsMeta, setValue]);

  const columns = useMemo(
    () => [
      Column.text('name', t('entity.taxonomy.station.name'), {
        filter: false,
        sort: false,
      }),
      Column.bool('deleted', t('entity.taxonomy.station.deleted._'), {
        width: 150,
        filter: false,
        sort: false,
        inverted: true,
        labels: [
          t('table.filter.all'),
          t('entity.taxonomy.station.deleted.true'),
          t('entity.taxonomy.station.deleted.false'),
          t('table.filter.unknown'),
        ],
      }),
      {
        dataIndex: 'direction',
        key: 'direction',
        width: 100,
        render: (_, record) => {
          if (record.direction === 'TUR') {
            return t('entity.taxonomy.station.tur');
          }
          if (record.direction === 'RETUR') {
            return t('entity.taxonomy.station.retur');
          }
          return '—';
        },
      },
    ],
    [t],
  );

  const content = useMemo(
    () =>
      stationsMeta.map((item, index) => {
        const station = stations.content.find((s) => s.id === item.stationId);
        return {
          index,
          ...station,
          direction: item.direction,
          position: index,
        };
      }),
    [stationsMeta, stations.content],
  );

  const moveRow = useCallback(
    (oldIndex, newIndex) => {
      const reordered = arrayMove(stationsMeta, oldIndex, newIndex).map(
        (item, idx) => ({
          ...item,
          position: idx,
        }),
      );
      setValue('stationsMeta', reordered);
    },
    [stationsMeta, setValue],
  );

  return (
    <DragSortingTable
      rowKey="index"
      moveRow={canEdit ? moveRow : undefined}
      columns={columns}
      dataSource={content}
      // loading={stations.loading}
    />
  );
};

export default StationsSelection;
